import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaSearch, FaSpinner, FaFileInvoice } from 'react-icons/fa';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Header from '../components/Header';
import Sidebar from '../components/Sidebar';
import './Pending.css';

// ══════════════════════════════════════
//  CONSTANTS
// ══════════════════════════════════════
const API = 'https://digitalapi.definiteassurance.com/api';
const COMPANY = 'Definite';
const ROWS_PER_PAGE = 20;

// ══════════════════════════════════════
//  HELPERS
// ══════════════════════════════════════
const formatter = (amount) =>
  Number(amount || 0).toFixed(2).replace(/\d(?=(\d{3})+\.)/g, '$&,');

const formatISODate = (input) => {
  try {
    if (typeof input === 'string') return input.split('T')[0];
    if (input instanceof Date) return input.toISOString().split('T')[0];
    return '--';
  } catch { return '--'; }
};

const buildPageNumbers = (current, total) => {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const pages = [1];
  const start = Math.max(2, current - 1);
  const end = Math.min(total - 1, current + 1);
  if (start > 2) pages.push('...');
  for (let i = start; i <= end; i++) pages.push(i);
  if (end < total - 1) pages.push('...');
  pages.push(total);
  return pages;
};

// ══════════════════════════════════════
//  COMPONENT
// ══════════════════════════════════════
export default function Pending() {
  const navigate = useNavigate();

  // ── Auth & agent ──
  const [credentials, setCredentials] = useState(null);
  const [isAuth, setIsAuth] = useState(false);
  const [agent, setAgent] = useState('');
  const [agentCode, setAgentCode] = useState('');
  const [agentName, setAgentName] = useState('');

  // ── Data ──
  const [proposals, setProposals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  //Proposal details modal
    const [selectedProposal, setSelectedProposal] = useState(null);
    const [clientName, setClientName] = useState('');
    const [clientEmail, setClientEmail] = useState('');
    const [clientPhone, setClientPhone] = useState('');
    const [clientPin, setClientPin] = useState(''); 
    const [insuredOID, setInsuredOID] = useState('');
    const [bodyType, setBodytype] = useState('');
    const [coption, setCoverOption] = useState('');
    const [make, setMake] = useState('');
    const [model, setModel] = useState('');
    const [capacity, setCapacity] = useState('');
    const [chasis, setChassis] = useState('');
    const [year, setYear] = useState('');
    const [sumInsured, setSumInsured] = useState('');
    const [engineNo, setEngineNo] = useState('');
    const [cc, setCC] = useState('');
    const [tonnage, setTonnage] = useState('');
    const [itemName, setItemName] = useState('');
    const [product, setProduct] = useState('');
    const [commencementDate, setCommencementDate] = useState('');
    const [expiryDate, setExpiryDate] = useState('');
    const [grossPremium, setGrossPremium] = useState('');
    const [dateDone, setDateDone] = useState('');
    const [narration, setNarration] = useState('');
    const [periodOfCover, setPeriodOfCover] = useState('');
    const [policyNo, setPolicyNo] = useState('');
    const [grossCommission, setGrossCommission] = useState('');
    const [phcf, setPHCF] = useState('');
    const [trainingLevy, setTrainingLevy] = useState('');
    




  // ══════════════════════════════════════
  //  AUTH HEADERS
  // ══════════════════════════════════════
  const authHeaders = useCallback(() => {
    const base = { 'Content-Type': 'application/json' };
    return credentials ? { ...base, Authorization: `Basic ${credentials}` } : base;
  }, [credentials]);

  // ══════════════════════════════════════
  //  INITIAL DATA FETCHING
  // ══════════════════════════════════════

  // Credentials
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/getCredentials?username=${COMPANY}`);
        const data = await res.json();
        setCredentials(btoa(`${data.Username}:${data.Password}`));
        setIsAuth(true);
      } catch (e) {
        console.error('Credentials fetch failed', e);
        toast.error('Authentication failed');
      }
    })();
  }, []);

  // Agent from localStorage
  useEffect(() => {
    const user = localStorage.getItem('definiteagent');
    if (user) setAgent(user);
    else navigate('/login');
  }, [navigate]);

  // Agent details
  useEffect(() => {
    if (!agent || !isAuth) return;
    (async () => {
      try {
        const res = await fetch(`${API}/getDealerdetails?email=${agent}`, {
          method: 'GET', headers: authHeaders(),
        });
        const data = await res.json();
        setAgentCode(data.OID);
        setAgentName(data.Dealer_name || agent);
      } catch (e) {
        console.error('getDealerdetails error', e);
      }
    })();
  }, [agent, isAuth, authHeaders]);

  // Fetch pending proposals
  useEffect(() => {
    if (!agentCode || !isAuth) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await fetch(`${API}/getPendingproposals?agent=${agentCode}`, {
          method: 'GET', headers: authHeaders(),
        });
        if (!res.ok) throw new Error(`API ${res.status}`);
        const data = await res.json();
        if (!cancelled) setProposals(Array.isArray(data) ? data : []);
      } catch (e) {
        console.error('Fetch proposals failed', e);
        if (!cancelled) toast.error('Failed to load pending proposals');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [agentCode, isAuth, authHeaders]);

  // ══════════════════════════════════════
  //  SEARCH & PAGINATION
  // ══════════════════════════════════════
  const filtered = useMemo(() => {
    if (!searchTerm.trim()) return proposals;
    const q = searchTerm.toLowerCase();
    return proposals.filter(
      (p) =>
        (p.Narration || '').toLowerCase().includes(q) ||
        (p.Dealer_name || '').toLowerCase().includes(q) ||
        (p.ProductDescription || '').toLowerCase().includes(q)
    );
  }, [proposals, searchTerm]);

  const totalPages = Math.ceil(filtered.length / ROWS_PER_PAGE);
  const currentRows = useMemo(
    () => filtered.slice((currentPage - 1) * ROWS_PER_PAGE, currentPage * ROWS_PER_PAGE),
    [filtered, currentPage] 
  );

  // Reset page when search changes
  useEffect(() => { setCurrentPage(1); }, [searchTerm]);

  // ══════════════════════════════════════
  //  RENDER
  // ══════════════════════════════════════
  return (
    <div className="pp-page">
      <Header userName={agentName || agent} />
      <Sidebar />
      <ToastContainer position="top-right" autoClose={3000} />

      <main className="pp-main">
        {/* ── Top row ── */}
        <div className="pp-top-row">
          <span className="pp-company">DEFINITE ASSURANCE</span>
          <span className="pp-breadcrumb">Proposals / Pending</span>
        </div>

        {/* ── Card ── */}
        <div className="pp-card">
          <div className="pp-card-header">
            <h3>
              <FaFileInvoice style={{ marginRight: 8, opacity: 0.7 }} />
              Pending Proposals
              <span className="pp-count">{filtered.length}</span>
            </h3>
          </div>

          {/* ── Toolbar ── */}
          <div className="pp-toolbar">
            <div className="pp-search-box">
              <FaSearch className="pp-search-icon" />
              <input
                type="text"
                placeholder="Search by registration, client or product…"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>

          {/* ── Table ── */}
          {loading ? (
            <div className="pp-loading">
              <FaSpinner className="pp-spinner" />
              <span>Loading pending proposals…</span>
            </div>
          ) : filtered.length === 0 ? (
            <div className="pp-empty">
              {searchTerm ? 'No proposals match your search.' : 'No pending proposals found.'}
            </div>
          ) : (
            <>
              <div className="pp-table-wrap">
                <table className="pp-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Registration</th>
                      <th>Client</th>
                      <th>Product</th>
                      <th className="pp-right">Gross Premium (KES)</th>
                      <th>Date Done</th>
                       <th>Paid Amount</th>
                      <th>Commencement</th>
                      <th>Expiry</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentRows.map((p, i) => (
                      <tr key={p.OID || i}>
                        <td>{(currentPage - 1) * ROWS_PER_PAGE + i + 1}</td>
                        <td className="pp-reg">{p.Narration || '--'}</td>
                        <td>{p.Dealer_name || '--'}</td>
                        <td>{p.ProductDescription || '--'}</td>
                        <td className="pp-right">{formatter(p.GrossPremium)}</td>
                        <td>{formatISODate(p.DateDone)}</td>
                        <td className="pp-right">{formatter(p.PaidAmount)}</td>
                        <td>{formatISODate(p.CommencementDate)}</td>
                        <td>{formatISODate(p.ExpiryDate)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* ── Pagination ── */}
              {totalPages > 1 && (
                <div className="pp-pagination">
                  <button disabled={currentPage === 1} onClick={() => setCurrentPage((p) => p - 1)}>‹ Prev</button>
                  {buildPageNumbers(currentPage, totalPages).map((pg, i) =>
                    pg === '...' ? (
                      <span key={`e${i}`} className="pp-ellipsis">…</span>
                    ) : (
                      <button key={pg} className={pg === currentPage ? 'active' : ''} onClick={() => setCurrentPage(pg)}>
                        {pg}
                      </button>
                    )
                  )}
                  <button disabled={currentPage === totalPages} onClick={() => setCurrentPage((p) => p + 1)}>Next ›</button>
                </div>
              )}
            </>
          )}
        </div>
      </main>
    </div>
  );
}
